import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextButton } from "@/components/common/text-button";
import { OutlineButton } from "@/components/common/outline-button";
import { TextInput } from "@/components/common/text-input";
import { Image } from "@/components/common/image";
import { Upload } from "@/components/common/upload";
import { VideoPlayer } from "@/components/common/video-player";
import { AudioPlayButton } from "@/components/common/audio-play-button";
import { CreateAudioModal } from "@/components/custom-audio";
import { Dropdown } from "@/components/ui/modal";
import { TemplateAudioModal } from "@/components/creation-modal-audio";
import { useToast } from "@/hooks/use-toast";
import { validateVideo } from "@/utils/video-validator";
import { validateImage } from "@/utils/image-validator";
import { cn } from "@/utils/cn";
import {
  ALLOW_IMAGE_TYPES,
  ALLOW_VIDEO_TYPES,
  ALLOW_IMAGE_MIN_SIZE,
  ALLOW_IMAGE_MAX_SIZE,
  ALLOW_VIDEO_MAX_SIZE,
  ALLOW_VIDEO_MIN_RESOLUTION,
  ALLOW_VIDEO_MAX_RESOLUTION,
  ALLOW_VIDEO_MIN_DURATION,
  ALLOW_VIDEO_MAX_DURATION,
} from "./constants";
import { AddIcon, AudioIcon, CloneIcon, PlayIcon, PlayingIcon, VoiceIcon } from "./assets";
import { customDigitalHumanHooks } from "@/manager/manager/custom-digitalhuman-manager";
import { ImageToVideoData } from "@/services/image-to-video";
import { SpendCreditIcon } from "../ui/icon";
import { WithCreditConsume } from "../common/with-credit-consumption";
import { validateVideoReason, VideoInvalid } from "@/utils/video-validator-reason";
import { ImageInvalid, validateImageReason } from "@/utils/image-validator-reason";
import { UPLOAD_ERROR_TYPE } from "@/hooks/use-upload";
import { CreditConsumeButton } from "../common/credit-consume-button";
import { SPEND_CREDITS_EVENT } from "@/manager/manager/subscription-manager";

export interface CreateFormProps {
  className?: string;
  // 上传文件的类型
  uploadType: "image" | "video";
  // 上传的文件
  file: File | null;
  // 上传文件的链接
  URL: string;
  // 重新上传
  onReUpload: (file: File) => void;
  // 更改表单
  onChange: (data: { actorName: string }) => void;
  // 提交表单
  onSubmit: (data: ImageToVideoData) => void;

}

export const CreateForm = ({
  className,
  uploadType,
  file,
  URL,
  onReUpload,
  onChange,
  onSubmit,
}: CreateFormProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  // 定制化数字人管理器
  const {
    name: actorName,
    message,
    voiceName,
    voiceURL,
    fileUri,
    isAllowCreate,
    creating,
    setActorName,
    setAudioName,
    setAudioUrl,
    init,
    createActor,
  } = customDigitalHumanHooks.useCustomDigitalHuman(uploadType);
  // 模板音频弹窗
  const [templateAudioModalOpen, setTemplateAudioModalOpen] = useState(false);
  // 创建音频弹窗
  const [createAudioModalOpen, setCreateAudioModalOpen] = useState(false);
  // 克隆音频弹窗
  const [cloneAudioModalOpen, setCloneAudioModalOpen] = useState(false);

  // 重新上传
  const handleReUpload = async (file: File) => {
    // 校验文件是否有效
    if (uploadType === "image") {
      const validResult = await validateImageReason(file, {
        allowedTypes: ALLOW_IMAGE_TYPES,
        minFileSize: ALLOW_IMAGE_MIN_SIZE,
        maxFileSize: ALLOW_IMAGE_MAX_SIZE,
      });
      if (!validResult.isValid) {
        let errorMessage = "custom_digital_human.upload_form.invalid_image_description"
        switch (validResult.errorType) {
          case ImageInvalid.FILE_SIZE:
            errorMessage = t("custom_digital_human.upload_form.invalid_image_filesize");
            break;
        }
        toast.default({
          title: t("custom_digital_human.upload_form.invalid_image"),
          description: t(errorMessage),
        });
        return;
      }
    } else if (uploadType === "video") {
      const validResult = await validateVideoReason(file, {
        allowedTypes: ALLOW_VIDEO_TYPES,
        maxFileSize: ALLOW_VIDEO_MAX_SIZE,
        minHeight: ALLOW_VIDEO_MIN_RESOLUTION,
        maxHeight: ALLOW_VIDEO_MAX_RESOLUTION,
        minDuration: ALLOW_VIDEO_MIN_DURATION,
        maxDuration: ALLOW_VIDEO_MAX_DURATION,
      });
      if (!validResult.isValid) {
        let errorMessage = "custom_digital_human.upload_form.invalid_video_description";
        switch (validResult.errorType) {
          case VideoInvalid.FILE_SIZE:
            errorMessage = t("custom_digital_human.upload_form.invalid_video_filesize");
            break;
          case VideoInvalid.RESOLUTION:
            errorMessage = t("custom_digital_human.upload_form.invalid_video_resolution");
            break;
          case VideoInvalid.DURATION:
            errorMessage = t("custom_digital_human.upload_form.invalid_video_duration");
            break;
        }
        toast.default({
          title: t("custom_digital_human.upload_form.invalid_video"),
          description: errorMessage,
        });
        return;
      }
    }
    onReUpload(file);
  };

  // 上传校验失败
  const handleInvalid = (reason?: keyof typeof UPLOAD_ERROR_TYPE) => {
    if (uploadType === "image") {
      let errorMessage = "custom_digital_human.upload_form.invalid_image_description"
      if (reason === UPLOAD_ERROR_TYPE.INVALID_MAX_SIZE) {
        errorMessage = t("custom_digital_human.upload_form.invalid_image_filesize")
      }
      toast.default({
        title: t("custom_digital_human.upload_form.invalid_image"),
        description: t(errorMessage),
      });
    } else if (uploadType === "video") {
      let errorMessage = "custom_digital_human.upload_form.invalid_video_description"
      if (reason === UPLOAD_ERROR_TYPE.INVALID_MAX_SIZE) {
        errorMessage = t("custom_digital_human.upload_form.invalid_video_filesize")
      }
      toast.default({
        title: t("custom_digital_human.upload_form.invalid_video"),
        description: t(errorMessage),
      });
    }
  };

  // 更新演员名称
  const handleActorNameChange = (name: string) => {
    setActorName(name);
    onChange({ actorName: name });
  };

  // 创建音频
  const handleAudioCreate = (audio: Audio) => {
    setAudioUrl(audio.audioUrl);
    setAudioName(audio.name);
    onChange({ actorName });
  };

  const handleSubmit = async ({ spendCredits }: { spendCredits: number }) => {
    const data = await createActor(uploadType, spendCredits);
    onSubmit(data as unknown as ImageToVideoData)
  }

  useEffect(() => {
    init({ actorName, audioUrl: voiceURL, audioName: voiceName });
  }, [actorName, voiceURL, voiceName]);

  const canSubmit = URL && actorName && voiceURL;
  const acceptedFileTypes =
    uploadType === "image"
      ? ALLOW_IMAGE_TYPES.join(",")
      : ALLOW_VIDEO_TYPES.join(",");
  const minFileSize = uploadType === "image" ? ALLOW_IMAGE_MIN_SIZE : 0;
  const maxFileSize =
    uploadType === "image" ? ALLOW_IMAGE_MAX_SIZE : ALLOW_VIDEO_MAX_SIZE;
  const options =
    uploadType === "image"
      ? [
        {
          value: "default",
          label: voiceName || t("custom_digital_human.create_form.select_actor_voice"),
          icon: <VoiceIcon />,
        },
        {
          value: "templates",
          label: t("custom_digital_human.create_form.audio_template"),
          icon: <AudioIcon />,
        },
        {
          value: "create",
          label: t("custom_digital_human.create_form.create_voice"),
          icon: <AddIcon />,
        },
      ]
      : [
        {
          value: "default",
          label: voiceName || t("custom_digital_human.create_form.select_actor_voice"),
          icon: <VoiceIcon />,
        },
        {
          value: "clone",
          label: t("custom_digital_human.create_form.clone"),
          icon: <CloneIcon />,
        },
        {
          value: "templates",
          label: t("custom_digital_human.create_form.audio_template"),
          icon: <AudioIcon />,
        },
        {
          value: "create",
          label: t("custom_digital_human.create_form.create_voice"),
          icon: <AddIcon />,
        },
      ];
  return (
    <>
      <div
        className={cn(
          "flex flex-col items-center justify-center gap-8",
          className
        )}
      >
        <div className={cn("flex flex-1 gap-6")}>
          {/* 左侧图片 */}
          {uploadType === "image" ? (
            <Image
              src={URL}
              alt="Actor"
              className="border h-80 aspect-[9/16] rounded-lg"
            />
          ) : (
            <VideoPlayer
              src={URL}
              className="border h-80 aspect-[9/16] rounded-lg"
            />
          )}

          {/* 右侧表单 */}
          <div className={cn("flex-1 space-y-4")}>
            {/* 演员名称 */}
            <div className="flex flex-col gap-3">
              <label className="text-sm text-[rgba(102,112,133,1)] font-medium">
                {t("custom_digital_human.create_form.name")}
              </label>
              <TextInput
                type="text"
                value={actorName}
                onChange={(e) => handleActorNameChange(e.target.value)}
                placeholder={t("custom_digital_human.create_form.name_placeholder")}
                error={Boolean(message)}
                helperText={message}
              />
            </div>

            {/* 音频选择 */}
            <div className="flex flex-col gap-3">
              <label className="text-sm text-[rgba(102,112,133,1)] font-medium">
                {t("custom_digital_human.create_form.voice")}
              </label>
              <div className={cn("flex items-center gap-2")}>
                <Dropdown
                  className="h-11"
                  width="21rem"
                  defaultValue={"default"}
                  value={"default"}
                  options={options}
                  showDefaultOption={false}
                  onValueChange={(value) => {
                    if (value === "templates") {
                      setTemplateAudioModalOpen(true);
                    } else if (value === "create") {
                      setCreateAudioModalOpen(true);
                    } else if (value === "clone") {
                      setCloneAudioModalOpen(true);
                    }
                  }}
                />
                <div className="flex justify-center items-center border border-[#d0d5dd] rounded-sm">
                  <AudioPlayButton
                    key={voiceURL}
                    disabled={!voiceURL}
                    url={voiceURL}
                    PlayIcon={PlayIcon}
                    PlayingIcon={PlayingIcon}
                    iconClassName={cn(
                      "w-11 h-11 p-[10px]",
                      voiceURL && "hover:text-[#404653]"
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center gap-4">
          <Upload
            acceptedFileTypes={acceptedFileTypes}
            minFileSize={minFileSize}
            maxFileSize={maxFileSize}
            onUpload={handleReUpload}
            onInvalid={handleInvalid}
          >
            <OutlineButton className="w-56" label={uploadType === "image" ? t("custom_digital_human.create_form.reupload_photo") : t("custom_digital_human.create_form.reupload_video")} />
          </Upload>
          <CreditConsumeButton
            className="w-56"
            label={t("common.create")}
            showSpendCredits={uploadType === 'image'}
            checkCredits={uploadType === 'image'}
            event={uploadType === 'image' ? SPEND_CREDITS_EVENT.image_number_person : null}
            disabled={!canSubmit || !isAllowCreate}
            loading={creating}
            onClick={handleSubmit}
          />
        </div>
      </div>
      <CreateAudioModal
        open={cloneAudioModalOpen}
        name={`${actorName} Voice`}
        file={file}
        videoURL={fileUri}
        uploadType={"video"}
        onOpenChange={setCloneAudioModalOpen}
        onSubmit={handleAudioCreate}
      />
      <CreateAudioModal
        open={createAudioModalOpen}
        onOpenChange={setCreateAudioModalOpen}
        onSubmit={handleAudioCreate}
      />
      <TemplateAudioModal
        file={file}
        cover={URL}
        uploadType={uploadType}
        open={templateAudioModalOpen}
        onOpenChange={setTemplateAudioModalOpen}
        onChange={({ audio }) => {
          // setVoiceId(audio.id);
          setAudioName(audio.name);
          setAudioUrl(audio.audioUrl);
          setTemplateAudioModalOpen(false);
        }}
      />
    </>
  );
};
